import { AxiosRequestConfig } from 'axios';
import { apiClient } from './axiosBaseQuery';

export interface AuthTokenProvider {
  getAccessToken: () => string | null;
  renewToken: () => Promise<void>;
  logout: () => void;
}

export const createAuthInterceptor = (tokenProvider: AuthTokenProvider) => {
  const requestInterceptor = apiClient.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const token = tokenProvider.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      throw error;
    },
  );

  // Response interceptor to handle 401 errors
  const responseInterceptor = apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await tokenProvider.renewToken();
          const newToken = tokenProvider.getAccessToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch (renewError) {
          tokenProvider.logout();
          throw renewError;
        }
      }

      throw error;
    },
  );

  return () => {
    apiClient.interceptors.request.eject(requestInterceptor);
    apiClient.interceptors.response.eject(responseInterceptor);
  };
};
